import { StructuredDataSchemaItem } from '@repo/shared/dtos/structured-data'
import { useCallback, useMemo } from 'react'

import {
  BeneficiaryListDataItem,
  BeneficiaryListTableProps,
} from '@/components/beneficiary/list/table/Table'
import { Beneficiary } from '@/store/beneficiary'
import { buildBeneficiaryStructuredDataView } from '@/store/helpers'

export const useBeneficiaryListTableData = ({
  beneficiaries,
  organization,
}: Pick<BeneficiaryListTableProps, 'beneficiaries' | 'organization'>) => {
  const getSections = useCallback(
    (beneficiary: Beneficiary) =>
      buildBeneficiaryStructuredDataView({
        display: organization.structuredDataDisplay,
        schema: organization.structuredDataSchema,
        values: beneficiary.structuredData.value,
      }),
    [organization],
  )

  const data: BeneficiaryListDataItem[] = useMemo(() => {
    return beneficiaries.map((beneficiary) =>
      transformBeneficiaryToDataItem(beneficiary, getSections),
    )
  }, [beneficiaries, getSections])

  return data
}

const transformBeneficiaryToDataItem = (
  beneficiary: Beneficiary,
  getSections: (beneficiary: Beneficiary) => ReturnType<typeof buildBeneficiaryStructuredDataView>,
): BeneficiaryListDataItem => {
  const sections = getSections(beneficiary)
  const dynamicData: Record<string, StructuredDataSchemaItem['defaultValue']> = {}

  sections.forEach((section) => {
    section.items.forEach((item) => {
      const columnId = item.id
      dynamicData[columnId] = item.value
    })
  })

  return {
    id: beneficiary.id,
    fullName: beneficiary.fullName,
    profile: beneficiary.profile.value,
    createdAt: beneficiary.createdAt,
    updatedAt: beneficiary.updatedAt,
    caseWorkerIds: beneficiary.caseWorkerIds,
    ...dynamicData,
  }
}
